import type { InputController, Tool } from '../input/InputController';
import { BUILDING_DEFS } from '../sim/constants';
import type { Simulation } from '../sim/sim';
import type { BuildingType } from '../sim/types';
import { el, formatMoney } from './dom';
import type { GlyphKind, IconFactory } from './icons';

interface ToolItem {
  kind: 'tool';
  tool: Tool;
  label: string;
  glyph: GlyphKind;
  costCents?: number;
  hotkey?: string;
  describe: string;
}

interface BuildingItem {
  kind: 'building';
  type: BuildingType;
}

type Item = ToolItem | BuildingItem;

interface Category {
  id: string;
  label: string;
  glyph: GlyphKind;
  items: Item[] | null; // null = direct tool, no flyout
  directTool?: Tool;
}

const CATEGORIES: Category[] = [
  {
    id: 'mine',
    label: 'Mine',
    glyph: 'mine',
    items: [
      { kind: 'tool', tool: 'dig', label: 'Dig', glyph: 'mine', hotkey: 'E', describe: 'Drag a rectangle to mark tiles for excavation. Click a mark to cancel it; right-drag removes marks.' },
      { kind: 'tool', tool: 'cancelDig', label: 'Cancel dig', glyph: 'cancel', hotkey: 'X', describe: 'Drag over marked tiles to cancel the orders.' },
    ],
  },
  {
    id: 'housing',
    label: 'Housing',
    glyph: 'housing',
    items: [
      { kind: 'building', type: 'trailer' },
      { kind: 'building', type: 'bunkhouse' },
      { kind: 'building', type: 'cottage' },
      { kind: 'building', type: 'familyHouse' },
      { kind: 'building', type: 'townhome' },
      { kind: 'building', type: 'apartment' },
    ],
  },
  {
    id: 'town',
    label: 'Town',
    glyph: 'town',
    items: [
      { kind: 'building', type: 'canteen' },
      { kind: 'building', type: 'generalStore' },
      { kind: 'building', type: 'saloon' },
      { kind: 'building', type: 'casino' },
      { kind: 'building', type: 'sheriff' },
    ],
  },
  {
    id: 'civic',
    label: 'Civic',
    glyph: 'civic',
    items: [
      { kind: 'building', type: 'school' },
      { kind: 'building', type: 'hospital' },
      { kind: 'building', type: 'communityHall' },
      { kind: 'building', type: 'chapel' },
      { kind: 'building', type: 'townHall' },
      { kind: 'building', type: 'bank' },
      { kind: 'building', type: 'park' },
      { kind: 'building', type: 'publicPool' },
      { kind: 'building', type: 'university' },
      { kind: 'building', type: 'railDepot' },
      { kind: 'building', type: 'passengerTerminal' },
      { kind: 'tool', tool: 'paved', label: 'Pave street', glyph: 'paved', costCents: 1_500, describe: 'Upgrade dirt roads to stone paving — twice the speed, and the town looks like it means to stay. Requires the charter.' },
    ],
  },
  {
    id: 'utilities',
    label: 'Utilities',
    glyph: 'utilities',
    items: [
      { kind: 'building', type: 'generator' },
      { kind: 'building', type: 'powerPole' },
      { kind: 'building', type: 'waterTank' },
      { kind: 'building', type: 'well' },
      { kind: 'building', type: 'solarArray' },
      { kind: 'building', type: 'nuclearPlant' },
      { kind: 'building', type: 'gridInterconnect' },
      { kind: 'tool', tool: 'pipe', label: 'Water pipe', glyph: 'pipe', costCents: 1_500, hotkey: 'U', describe: 'Drag to route a pipe from A to B — hold Shift to paint freehand, right-drag to tear out for half back. Pipes run under buildings.' },
      { kind: 'tool', tool: 'cable', label: 'Buried cable', glyph: 'cable', costCents: 3_500, hotkey: 'C', describe: 'Underground power line — drag routes it, Shift paints, right-drag salvages. Pricier than poles, but invisible and runs under buildings.' },
    ],
  },
  {
    id: 'industry',
    label: 'Industry',
    glyph: 'industry',
    items: [
      { kind: 'building', type: 'oreDepot' },
      { kind: 'tool', tool: 'road', label: 'Haul road', glyph: 'road', costCents: 500, hotkey: 'R', describe: 'Drag to route a road from A to B — Shift paints freehand, right-drag tears out. Workers move much faster on roads.' },
      { kind: 'tool', tool: 'reclaim', label: 'Reclaim land', glyph: 'reclaim', describe: 'Drag over old pits to fill them back to grade — $8 per buried block. Filled land takes farms, and homes nearby breathe easier. Requires the charter.' },
      { kind: 'building', type: 'cornFarm' },
      { kind: 'building', type: 'wheatFarm' },
      { kind: 'building', type: 'cattleRanch' },
      { kind: 'building', type: 'warehouse' },
      { kind: 'building', type: 'freightOffice' },
      { kind: 'building', type: 'goodsMarket' },
      { kind: 'building', type: 'glassworks' },
      { kind: 'building', type: 'chipFab' },
    ],
  },
  { id: 'demolish', label: 'Demolish', glyph: 'demolish', items: null, directTool: 'demolish' },
];

const TOOL_CATEGORY: Record<string, string> = {
  dig: 'mine',
  cancelDig: 'mine',
  pipe: 'utilities',
  cable: 'utilities',
  road: 'industry',
  paved: 'civic',
  reclaim: 'industry',
  demolish: 'demolish',
};

/** Gated tools: true = locked. One table instead of per-tool if-blocks. */
const TOOL_LOCKS: Record<string, (view: Simulation) => boolean> = {
  paved: (view) => view.state.meta.townName === null,
  reclaim: (view) => view.state.meta.townName === null,
};

function buildingCategory(type: BuildingType): string {
  for (const category of CATEGORIES) {
    if (category.items?.some((item) => item.kind === 'building' && item.type === type)) return category.id;
  }
  return '';
}

/** Left icon strip + flyout panels. All imagery comes from IconFactory. */
export class Sidebar {
  private readonly input: InputController;
  private readonly view: Simulation;
  private readonly icons: IconFactory;
  private readonly strip: HTMLElement;
  private readonly flyout: HTMLElement;
  private readonly tooltip: HTMLElement;
  private readonly categoryButtons = new Map<string, HTMLButtonElement>();
  private openCategory: string | null = null;
  private lastAffordKey = '';

  constructor(
    root: HTMLElement,
    input: InputController,
    view: Simulation,
    icons: IconFactory,
    gameCanvas: HTMLCanvasElement,
  ) {
    this.input = input;
    this.view = view;
    this.icons = icons;

    this.strip = el('div', { class: 'panel sidebar' });
    for (const category of CATEGORIES) {
      const button = el(
        'button',
        {
          class: 'cat',
          title: category.label,
          'data-tutorial': `cat-${category.id}`,
          onclick: () => this.onCategoryClick(category),
        },
        icons.glyph(category.glyph),
      );
      this.categoryButtons.set(category.id, button);
      this.strip.append(button);
    }
    this.flyout = el('div', { class: 'panel flyout' });
    this.flyout.style.display = 'none';
    this.tooltip = el('div', { class: 'panel build-tooltip' });
    this.tooltip.style.display = 'none';
    root.append(this.strip, this.flyout, this.tooltip);

    gameCanvas.addEventListener('pointerdown', () => this.close());
    window.addEventListener('keydown', (event) => {
      if (event.code === 'KeyQ') this.close();
    });
  }

  private onCategoryClick(category: Category): void {
    if (category.items === null) {
      this.close();
      const active = this.input.tool === category.directTool;
      this.input.setTool(active ? 'idle' : (category.directTool as Tool));
      return;
    }
    if (this.openCategory === category.id) {
      this.close();
      return;
    }
    this.open(category);
  }

  private open(category: Category): void {
    this.openCategory = category.id;
    this.lastAffordKey = '';
    this.flyout.replaceChildren(el('div', { class: 'flyout-title' }, category.label));
    const grid = el('div', { class: 'flyout-grid' });
    for (const item of category.items ?? []) {
      grid.append(this.makeTile(item));
    }
    this.flyout.append(grid);
    this.flyout.style.display = '';
    const button = this.categoryButtons.get(category.id)!;
    const top = this.strip.offsetTop + button.offsetTop;
    this.flyout.style.top = `${Math.max(40, Math.min(top, window.innerHeight - 200))}px`;
    this.refreshStates();
    this.syncActive();
  }

  close(): void {
    if (this.openCategory === null) return;
    this.openCategory = null;
    this.flyout.style.display = 'none';
    this.hideTooltip();
    this.syncActive();
  }

  private makeTile(item: Item): HTMLElement {
    const isBuilding = item.kind === 'building';
    const label = isBuilding ? BUILDING_DEFS[item.type].label : item.label;
    const cost = isBuilding ? BUILDING_DEFS[item.type].costCents : (item.costCents ?? 0);
    const icon = isBuilding ? this.icons.buildingIcon(item.type) : this.icons.glyph(item.glyph);
    const tile = el(
      'button',
      {
        class: 'tile',
        onclick: () => {
          if (tile.classList.contains('locked')) return;
          if (isBuilding) this.input.setTool('place', item.type);
          else this.input.setTool(item.tool);
          this.openCategory = null;
          this.flyout.style.display = 'none';
          this.hideTooltip();
        },
      },
      el('span', { class: 'tile-icon' }, icon),
      el('span', { class: 'tile-cost' }, cost > 0 ? formatMoney(cost) : label),
    );
    tile.dataset['item'] = isBuilding ? `b:${item.type}` : `t:${item.tool}`;
    tile.addEventListener('mouseenter', () => this.showTooltip(tile, item));
    tile.addEventListener('mouseleave', () => this.hideTooltip());
    return tile;
  }

  private showTooltip(tile: HTMLElement, item: Item): void {
    const lines: (HTMLElement | string)[] = [];
    if (item.kind === 'building') {
      const def = BUILDING_DEFS[item.type];
      lines.push(el('div', { class: 'title' }, def.label));
      lines.push(el('div', {}, formatMoney(def.costCents)));
      const facts: string[] = [];
      if (def.beds) facts.push(`sleeps ${def.beds}`);
      if (def.foodCap) facts.push(`feeds ${def.foodCap}`);
      if (def.drinkCap) facts.push(`serves ${def.drinkCap}`);
      if (def.gambleCap) facts.push(`seats ${def.gambleCap}`);
      if (def.powerSupply) facts.push(`power +${def.powerSupply}`);
      if (def.powerDraw) facts.push(`power -${def.powerDraw}`);
      if (def.waterPerDay) facts.push(`water ${def.waterPerDay}/day`);
      if (def.wellYieldPerDay) facts.push(`water +${def.wellYieldPerDay}/day`);
      if (def.staffRequired) facts.push(`staff ${def.staffRequired}${def.staffWaged ? ' (waged)' : ''}`);
      if (def.fuelCentsPerWeek) facts.push(`fuel ${formatMoney(def.fuelCentsPerWeek)}/wk`);
      if (def.produces) facts.push(`${def.produces.good} +${def.produces.unitsPerDay}/day`);
      if (def.consumes) facts.push(`eats ${def.consumes.good} ${def.consumes.unitsPerDay}/day`);
      if (def.goodsCapacityUnits) facts.push(`stores ${def.goodsCapacityUnits} goods`);
      if (facts.length > 0) lines.push(el('div', { class: 'muted' }, facts.join(' · ')));
      if (tile.classList.contains('locked')) {
        const hint =
          def.lockHint ?? (def.unlockedBy === 'charter' ? 'Locked — requires the town charter' : 'Locked — keep playing to unlock');
        lines.push(el('div', { class: 'warn' }, hint));
      } else if (tile.classList.contains('dim')) {
        lines.push(el('div', { class: 'warn' }, 'Not enough cash'));
      }
    } else {
      lines.push(el('div', { class: 'title' }, item.label + (item.hotkey ? ` (${item.hotkey})` : '')));
      if (item.costCents) lines.push(el('div', {}, `${formatMoney(item.costCents)} per tile`));
      lines.push(el('div', { class: 'muted' }, item.describe));
    }
    this.tooltip.replaceChildren(...lines);
    this.tooltip.style.display = '';
    const rect = tile.getBoundingClientRect();
    this.tooltip.style.left = `${rect.right + 8}px`;
    this.tooltip.style.top = `${Math.min(rect.top, window.innerHeight - 120)}px`;
  }

  private hideTooltip(): void {
    this.tooltip.style.display = 'none';
  }

  /** Highlight the category owning the active tool. */
  syncActive(): void {
    const tool = this.input.tool;
    let activeId = '';
    if (tool === 'place' && this.input.placeType) activeId = buildingCategory(this.input.placeType);
    else if (tool !== 'idle') activeId = TOOL_CATEGORY[tool] ?? '';
    for (const [id, button] of this.categoryButtons) {
      button.classList.toggle('active', id === activeId || id === this.openCategory);
    }
  }

  /** Dim unaffordable tiles, lock gated ones. Called when cash/unlocks change. */
  refreshStates(): void {
    if (this.openCategory === null) return;
    const cash = this.view.state.economy.cashCents;
    const delivered = this.view.state.letters.delivered;
    const key = `${cash}:${delivered.length}:${this.view.state.meta.townName}`;
    if (key === this.lastAffordKey) return;
    this.lastAffordKey = key;
    for (const tile of this.flyout.querySelectorAll<HTMLElement>('.tile')) {
      const data = tile.dataset['item'] ?? '';
      if (data.startsWith('t:')) {
        const lock = TOOL_LOCKS[data.slice(2)];
        if (lock) tile.classList.toggle('locked', lock(this.view));
        continue;
      }
      if (!data.startsWith('b:')) continue;
      const type = data.slice(2) as BuildingType;
      const def = BUILDING_DEFS[type];
      const locked =
        def.unlockedBy === 'charter'
          ? this.view.state.meta.townName === null
          : Boolean(def.unlockedBy) && !delivered.some((letter) => letter.defId === def.unlockedBy);
      tile.classList.toggle('locked', locked);
      tile.classList.toggle('dim', !locked && def.costCents > cash);
    }
  }
}
